<template>
  <div class="modal-overlay" @click="close">
    <div class="modal" @click.stop>
      <h3>Предложить слот</h3>
      
      <!-- Двухколоночный layout -->
      <div class="modal-body">
        <!-- Левая колонка: данные клиента -->
        <div class="client-panel">
          <div class="panel-title">Заявка клиента</div>
          
          <div class="info-row">
            <span class="label">Клиент:</span>
            <strong class="value">{{ entry?.client_name }}</strong>
          </div>
          
          <div class="info-row" v-if="entry?.preferred_date">
            <span class="label">Желаемая дата:</span>
            <span class="value">{{ formatDate(entry.preferred_date) }}</span>
            <span v-if="entry.date_flexibility_days" class="flex-badge">
              ±{{ entry.date_flexibility_days }}д
            </span>
          </div>
          
          <div class="info-row" v-if="entry?.preferred_time_start">
            <span class="label">Желаемое время:</span>
            <span class="value">
              {{ formatTime(entry.preferred_time_start) }}
              <span v-if="entry.preferred_time_end">
                – {{ formatTime(entry.preferred_time_end) }}
              </span>
            </span>
          </div>
          
          <div class="info-row" v-if="entry?.preferred_master_name">
            <span class="label">Предпочтительный мастер:</span>
            <span class="value">{{ entry.preferred_master_name }}</span>
          </div>
          <div class="info-row" v-else-if="entry?.any_master_allowed">
            <span class="label">Предпочтительный мастер:</span>
            <span class="value any-master">Любой мастер</span>
          </div>

          <!-- Услуги -->
          <div class="info-row services-row" v-if="entry?.services && entry.services.length">
            <span class="label">Услуги:</span>
            <div class="services-list">
              <span 
                v-for="svc in entry.services" 
                :key="svc.id"
                class="service-chip"
                :style="getServiceStyle(svc)"
              >
                {{ svc.name }}
                <small v-if="svc.duration_minutes">({{ svc.duration_minutes }}мин)</small>
              </span>
            </div>
          </div>
          
          <div class="info-row" v-if="totalDurationText">
            <span class="label">Общая длительность:</span>
            <span class="value duration-highlight">{{ totalDurationText }}</span>
          </div>

          <div class="info-row" v-if="entry?.notes">
            <span class="label">Комментарий:</span>
            <span class="value notes">{{ entry.notes }}</span>
          </div>
        </div>

        <!-- Правая колонка: выбор слота -->
        <div class="slot-panel">
          <div class="panel-title">Выбор слота</div>
          
          <div class="field">
            <label>Дата слота:</label>
            <input 
              type="date" 
              v-model="slotDate"
              @change="onDateChange"
              :min="minDate"
            />
            <div v-if="slotDateError" class="field-error">
              {{ slotDateError }}
            </div>
          </div>

          <div class="field">
            <label>Мастер:</label>
            <select v-model="selectedMaster" @change="onMasterChange">
              <option value="">Выберите мастера</option>
              <option 
                v-for="m in filteredMasters" 
                :key="m.id" 
                :value="m.id"
              >
                {{ m.name }}
              </option>
            </select>
          </div>

          <div class="field">
            <label>Время:</label>
            <div v-if="loadingSlots" class="loading-state">
              <span class="spinner"></span>
              Загрузка слотов...
            </div>
            <div v-else-if="slotDateError" class="no-slots-state">
              <div class="no-slots-title">Дата недоступна</div>
              <div class="no-slots-hint">{{ slotDateError }}</div>
            </div>
            <div v-else-if="availableSlots.length === 0" class="no-slots-state">
              <div class="no-slots-title">Нет доступных слотов</div>
              <div class="no-slots-hint" v-if="slotDate && selectedMaster">
                На {{ formatDate(slotDate) }} у мастера нет свободного времени.
              </div>
              <div class="no-slots-hint" v-else>
                Выберите дату и мастера
              </div>
              <!-- Предлагаем ближайшие даты -->
              <div class="suggested-dates" v-if="suggestedDates.length">
                <div class="suggested-label">Ближайшие даты со слотами:</div>
                <button 
                  v-for="d in suggestedDates" 
                  :key="d"
                  class="suggested-date-btn"
                  @click="selectSuggestedDate(d)"
                >
                  {{ formatDate(d) }}
                </button>
              </div>
            </div>
            <div v-else class="slots-grid">
              <button
                v-for="slot in availableSlots"
                :key="slot"
                :class="['slot-btn', { active: selectedSlot === slot }]"
                @click="selectedSlot = slot"
              >
                {{ slot }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="offerError" class="offer-error" role="alert">{{ offerError }}</div>

      <div class="modal-actions">
        <button 
          @click="confirmOffer" 
          class="btn-confirm"
          :disabled="!canConfirm || offering"
        >
          {{ offering ? 'Предлагаем...' : 'Предложить слот' }}
        </button>
        <button @click="close" class="btn-cancel">Отмена</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useCompanyStore } from '../stores/company'
import { useWaitlistStore } from '../stores/waitlist'
import { api } from '../stores/auth'

const props = defineProps({
  entry: { type: Object, default: null }
})

const emit = defineEmits(['close', 'offered'])

const company = useCompanyStore()
const waitlistStore = useWaitlistStore()

const slotDate = ref('')
const selectedMaster = ref('')
const selectedSlot = ref('')
const availableSlots = ref([])
const loadingSlots = ref(false)
const offering = ref(false)
const suggestedDates = ref([])
const serviceMasters = ref([])
const slotDateError = ref('')
const offerError = ref('')

// === MIN DATE (сегодня, без timezone-багов) ===
const minDate = computed(() => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
})

// === ПРОВЕРКА ДАТЫ ===
function validateDate(dateStr) {
  slotDateError.value = ''
  if (!dateStr) return false
  
  // Сравниваем строки YYYY-MM-DD
  if (dateStr < minDate.value) {
    slotDateError.value = 'Нельзя выбрать прошедшую дату'
    availableSlots.value = []
    selectedSlot.value = ''
    return false
  }
  return true
}

// === ЗАГРУЗКА МАСТЕРОВ ПО УСЛУГАМ ЗАЯВКИ ===
async function loadServiceMasters() {
  if (!props.entry?.services?.length) {
    serviceMasters.value = []
    return
  }
  try {
    const serviceIds = [...new Set(props.entry.services.map(s => s.id).filter(Boolean))]
    if (serviceIds.length === 0) {
      serviceMasters.value = []
      return
    }
    
    const allMasters = []
    for (const serviceId of serviceIds) {
      const { data } = await api.get(`/services/${serviceId}/masters`)
      allMasters.push(data.map(m => m.id))
    }
    
    if (allMasters.length === 0) {
      serviceMasters.value = []
      return
    }
    
    let validMasterIds = new Set(allMasters[0])
    for (let i = 1; i < allMasters.length; i++) {
      validMasterIds = new Set(allMasters[i].filter(id => validMasterIds.has(id)))
    }
    
    serviceMasters.value = Array.from(validMasterIds)
  } catch (err) {
    console.error('❌ Ошибка загрузки мастеров по услугам:', err)
    serviceMasters.value = []
  }
}

// === ФИЛЬТР МАСТЕРОВ ===
const filteredMasters = computed(() => {
  if (!props.entry) return []
  
  if (props.entry.preferred_master_id && !props.entry.any_master_allowed) {
    const master = company.masters.find(m => m.id === props.entry.preferred_master_id)
    return master ? [master] : []
  }
  
  if (serviceMasters.value.length > 0) {
    return company.masters.filter(m => serviceMasters.value.includes(m.id))
  }
  
  return company.masters
})

const canConfirm = computed(() => {
  return slotDate.value && selectedMaster.value && selectedSlot.value && !slotDateError.value
})

function formatDate(dateInput) {
  if (!dateInput) return ''
  if (dateInput instanceof Date) {
    const y = dateInput.getFullYear()
    const m = String(dateInput.getMonth() + 1).padStart(2, '0')
    const d = String(dateInput.getDate()).padStart(2, '0')
    return `${d}.${m}.${y}`
  }
  if (typeof dateInput === 'string') {
    let dateStr = dateInput
    if (dateInput.includes('T')) dateStr = dateInput.split('T')[0]
    const parts = dateStr.split('-').map(Number)
    if (parts.length === 3 && !parts.some(isNaN)) {
      const [y, m, d] = parts
      return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`
    }
  }
  return String(dateInput)
}

function formatTime(timeStr) {
  if (!timeStr) return ''
  return timeStr.slice(0, 5)
}

function getServiceStyle(svc) {
  const color = svc.color || '#6366f1'
  return {
    background: color + '15',
    color: color,
    border: `1px solid ${color}40`
  }
}

const totalDurationText = computed(() => {
  const services = props.entry?.services || []
  const total = services.reduce((sum, s) => sum + (s.duration_minutes || 0), 0)
  if (total === 0) {
    const mins = props.entry?.total_duration
    if (!mins) return ''
    if (mins >= 60) {
      const h = Math.floor(mins / 60)
      const m = mins % 60
      return m > 0 ? `${h}ч ${m}мин` : `${h}ч`
    }
    return `${mins}мин`
  }
  if (total >= 60) {
    const h = Math.floor(total / 60)
    const m = total % 60
    return m > 0 ? `${h}ч ${m}мин` : `${h}ч`
  }
  return `${total}мин`
})

async function onDateChange() {
  selectedSlot.value = ''
  if (!validateDate(slotDate.value)) return
  if (!slotDate.value || !selectedMaster.value) return
  await loadSlots()
  await loadSuggestedDates()
}

async function onMasterChange() {
  selectedSlot.value = ''
  if (!validateDate(slotDate.value)) return
  if (!slotDate.value || !selectedMaster.value) return
  await loadSlots()
}

async function fetchDaySlots(date) {
  // Слоты считает сервер по услугам самой заявки: общая длительность учтена,
  // занятое время, перерывы и конец рабочего дня исключены
  const { data } = await api.get(`/waitlist/${props.entry.id}/slots`, {
    params: { date, master_id: selectedMaster.value }
  })
  let slots = Array.isArray(data?.slots) ? data.slots : []
  const ps = props.entry?.preferred_time_start ? props.entry.preferred_time_start.slice(0, 5) : null
  const pe = props.entry?.preferred_time_end ? props.entry.preferred_time_end.slice(0, 5) : null
  if (ps && pe) {
    slots = slots.filter(sl => sl.time >= ps && (sl.end_time || sl.time) <= pe)
  }
  return slots
}

async function loadSlots() {
  if (!validateDate(slotDate.value)) return
  
  loadingSlots.value = true
  availableSlots.value = []
  selectedSlot.value = ''
  suggestedDates.value = []
  
  try {
    const slots = await fetchDaySlots(slotDate.value)
    availableSlots.value = slots.filter(s => s.available !== false).map(s => s.time)
  } catch (err) {
    console.error('Ошибка загрузки слотов:', err)
    availableSlots.value = []
  } finally {
    loadingSlots.value = false
  }
}

async function loadSuggestedDates() {
  suggestedDates.value = []
  if (!selectedMaster.value) return
  
  try {
    const [year, month, day] = minDate.value.split('-').map(Number)
    const dates = []
    for (let i = 0; i < 14; i++) {
      const d = new Date(year, month - 1, day + i)
      dates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`)
    }
    const results = await Promise.all(dates.map(date =>
      fetchDaySlots(date)
        .then(sl => ({ date, hasSlots: sl.some(s => s.available !== false) }))
        .catch(() => ({ date, hasSlots: false }))
    ))
    suggestedDates.value = results.filter(r => r.hasSlots).map(r => r.date).slice(0, 3)
  } catch (err) {
    console.error('Ошибка загрузки предложенных дат:', err)
  }
}

function selectSuggestedDate(date) {
  slotDate.value = date
  onDateChange()
}

async function confirmOffer() {
  if (!canConfirm.value) return
  
  offering.value = true
  offerError.value = ''
  try {
    await waitlistStore.offerSlot(props.entry.id, slotDate.value, selectedSlot.value, selectedMaster.value)
    emit('offered', { entryId: props.entry.id, date: slotDate.value, time: selectedSlot.value })
    close()
  } catch (err) {
    const data = err.response?.data || {}
    offerError.value = data.error || 'Не удалось предложить слот'
    if (data.code === 'SLOT_UNAVAILABLE') {
      // время успели занять: обновляем список, выбор сбрасываем
      await loadSlots()
    }
  } finally {
    offering.value = false
  }
}

function close() {
  emit('close')
}

// === ИНИЦИАЛИЗАЦИЯ ===
function initFromEntry(entry) {
  if (!entry) return
  
  // Дата: предпочтительная или сегодня, но не раньше minDate
  if (entry.preferred_date) {
    let pd = entry.preferred_date
    if (typeof pd === 'string' && pd.includes('T')) pd = pd.split('T')[0]
    slotDate.value = pd < minDate.value ? minDate.value : pd
  } else {
    slotDate.value = minDate.value
  }
  
  // Мастер
  if (entry.preferred_master_id) {
    const masterAvailable = filteredMasters.value.find(m => m.id === entry.preferred_master_id)
    if (masterAvailable) {
      selectedMaster.value = entry.preferred_master_id
    } else if (filteredMasters.value.length > 0) {
      selectedMaster.value = filteredMasters.value[0].id
    }
  } else if (filteredMasters.value.length > 0) {
    selectedMaster.value = filteredMasters.value[0].id
  }
  
  validateDate(slotDate.value)
  
  if (slotDate.value && selectedMaster.value && !slotDateError.value) {
    loadSlots()
    loadSuggestedDates()
  }
}

onMounted(async () => {
  await loadServiceMasters()
  initFromEntry(props.entry)
})

watch(() => props.entry, async (newEntry) => {
  await loadServiceMasters()
  initFromEntry(newEntry)
}, { immediate: true })
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

.modal h3 {
  margin: 0 0 1.25rem;
  color: #1e293b;
  font-size: 1.2rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
}

/* Двухколоночный layout */
.modal-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.25rem;
}

@media (max-width: 640px) {
  .modal-body {
    grid-template-columns: 1fr;
  }
}

.panel-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid #e5e7eb;
}

.client-panel {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1rem;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
  font-size: 0.9rem;
}

.info-row .label {
  color: #6b7280;
  min-width: 130px;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.info-row .value {
  color: #1e293b;
  font-weight: 500;
  flex: 1;
}

.value.any-master {
  color: #9ca3af;
  font-style: italic;
}

.duration-highlight {
  color: #059669;
  font-weight: 700;
}

.flex-badge {
  font-size: 0.75rem;
  color: var(--brand-primary-active);
  background: #f3e8ff;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-weight: 500;
}

.services-row {
  flex-direction: column;
}

.services-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.service-chip {
  font-size: 0.78rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 500;
}

.service-chip small {
  font-weight: 400;
  opacity: 0.8;
}

.notes {
  color: #6b7280;
  font-style: italic;
  font-size: 0.85rem;
}

/* Правая панель */
.slot-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.field label {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
}

.field input,
.field select {
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1rem;
  background: white;
}

.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 3px rgba(3, 28, 56, 0.1);
}

.field-error {
  color: #dc2626;
  font-size: 0.8rem;
  margin-top: 0.25rem;
  padding: 0.4rem 0.6rem;
  background: #fef2f2;
  border-radius: 6px;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #6b7280;
  font-size: 0.9rem;
  background: #f8fafc;
  border-radius: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--brand-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.no-slots-state {
  text-align: center;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
}

.no-slots-icon {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.no-slots-title {
  color: #4b5563;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.no-slots-hint {
  color: #9ca3af;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}

.suggested-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 0.5rem;
}

.suggested-label {
  width: 100%;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.suggested-date-btn {
  padding: 0.4rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s;
}

.suggested-date-btn:hover {
  background: var(--brand-primary);
  color: white;
  border-color: var(--brand-primary);
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.slot-btn {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  font-weight: 500;
}

.slot-btn:hover {
  background: #f3f4f6;
  border-color: var(--brand-primary);
}

.slot-btn.active {
  background: var(--brand-primary);
  color: white;
  border-color: var(--brand-primary);
}

.modal-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.btn-confirm {
  flex: 1;
  padding: 0.75rem;
  background: var(--brand-primary);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 600;
}

.btn-confirm:hover:not(:disabled) {
  background: var(--brand-primary-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(3, 28, 56, 0.3);
}

.btn-confirm:disabled {
  background: #c4d3e6;
  cursor: not-allowed;
}

.btn-cancel {
  flex: 1;
  padding: 0.75rem;
  background: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

@media (max-width: 480px) {
  .slots-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .info-row {
    flex-wrap: wrap;
  }
  
  .info-row .label {
    min-width: auto;
    width: 100%;
  }
  
  .modal {
    padding: 1rem;
  }
}
.offer-error {
  margin-bottom: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 0.9rem;
}
</style>